import { FormEvent, useCallback, useEffect, useState } from "react";
import type { User } from "@firebase/auth";
import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from "@firebase/auth";
import { BoardPortal } from "./BoardPortal";
import { auth, googleProvider } from "./firebase";

function signInMessage(code: string): string {
  if (code === "auth/invalid-credential" || code === "auth/wrong-password" || code === "auth/user-not-found") {
    return "Email or password is incorrect.";
  }
  if (code === "auth/email-already-in-use") {
    return "An account with this email already exists. Sign in instead.";
  }
  if (code === "auth/weak-password") {
    return "That password is too weak. Use at least 8 characters with some variety.";
  }
  if (code === "auth/too-many-requests") {
    return "Too many attempts. Wait a few minutes and try again.";
  }
  if (code === "auth/operation-not-allowed") {
    return "Email and password sign-in is not enabled yet. Contact the administrator.";
  }
  return "Sign-in could not be completed. Please try again.";
}

export function App() {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [registering, setRegistering] = useState(false);
  // null = checking with the API; the server-side allowlist is the authority.
  const [authorised, setAuthorised] = useState<boolean | null>(null);

  useEffect(() => onAuthStateChanged(auth, (next) => {
    setUser(next);
    setAuthorised(null);
    setReady(true);
  }), []);

  const apiFetch = useCallback<typeof fetch>(async (input, init = {}) => {
    const token = await auth.currentUser?.getIdToken();
    const headers = new Headers(init.headers);
    if (token) headers.set("authorization", `Bearer ${token}`);
    return fetch(input, { ...init, headers });
  }, []);

  const probeAccess = useCallback(async () => {
    try {
      const response = await apiFetch("/api/records");
      setAuthorised(response.status !== 401 && response.status !== 403);
    } catch {
      setAuthorised(true); // network failure is not an access verdict
    }
  }, [apiFetch]);

  useEffect(() => {
    if (user) void probeAccess();
  }, [user, probeAccess]);

  async function signInWithGoogle(): Promise<void> {
    setError("");
    setNotice("");
    setBusy(true);
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (caught) {
      setError(signInMessage((caught as { code?: string }).code ?? ""));
    } finally {
      setBusy(false);
    }
  }

  async function submitEmailForm(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError("");
    setNotice("");
    const values = new FormData(event.currentTarget);
    const email = String(values.get("email") || "").trim();
    const password = String(values.get("password") || "");
    if (!email || !password) return;
    setBusy(true);
    try {
      if (registering) {
        const created = await createUserWithEmailAndPassword(auth, email, password);
        await sendEmailVerification(created.user);
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
    } catch (caught) {
      setError(signInMessage((caught as { code?: string }).code ?? ""));
    } finally {
      setBusy(false);
    }
  }

  async function resendVerification(): Promise<void> {
    if (!auth.currentUser) return;
    setBusy(true);
    try {
      await sendEmailVerification(auth.currentUser);
      setNotice("Verification email sent. Check the inbox (and spam folder).");
    } catch {
      setNotice("Could not send the email just now. Wait a minute and try again.");
    } finally {
      setBusy(false);
    }
  }

  async function recheckAccess(): Promise<void> {
    if (!auth.currentUser) return;
    setBusy(true);
    try {
      await auth.currentUser.reload();
      await auth.currentUser.getIdToken(true);
      setUser(auth.currentUser);
      setAuthorised(null);
      await probeAccess();
    } finally {
      setBusy(false);
    }
  }

  if (!ready) return <main className="auth-page"><div className="auth-status">Opening Atlas…</div></main>;

  if (!user) {
    return (
      <main className="auth-page">
        <section className="auth-card" aria-labelledby="sign-in-title">
          <span className="mark mark-atlas auth-mark">A</span>
          <span className="eyebrow">FFP HOLDINGS</span>
          <h1 id="sign-in-title">{registering ? "Create your account" : "Sign in to Atlas"}</h1>
          <p>The private board workspace for governed portfolio reporting, decisions, and actions.</p>
          {error && <div className="auth-error" role="alert">{error}</div>}
          <form className="auth-form" onSubmit={(event) => void submitEmailForm(event)}>
            <input className="field-input" name="email" type="email" autoComplete="email" placeholder="Email" required />
            <input
              className="field-input"
              name="password"
              type="password"
              autoComplete={registering ? "new-password" : "current-password"}
              placeholder={registering ? "Choose a password (8+ characters)" : "Password"}
              minLength={registering ? 8 : undefined}
              required
            />
            <button className="primary-button auth-button" type="submit" disabled={busy}>
              {busy ? "Working…" : registering ? "Create account" : "Sign in"}
            </button>
          </form>
          <button className="text-button auth-switch" type="button" onClick={() => { setRegistering(!registering); setError(""); }}>
            {registering ? "Already have an account? Sign in" : "New here? Create your account"}
          </button>
          <div className="auth-divider"><span>or</span></div>
          <button className="compact-button auth-button" type="button" disabled={busy} onClick={() => void signInWithGoogle()}>
            Continue with Google
          </button>
          <small>
            Access is restricted to authorised FFP Holdings accounts. Creating an
            account does not grant access until the administrator authorises it.
          </small>
        </section>
      </main>
    );
  }

  if (authorised === null) {
    return <main className="auth-page"><div className="auth-status">Checking access…</div></main>;
  }

  if (!authorised && !user.emailVerified) {
    return (
      <main className="auth-page">
        <section className="auth-card" aria-labelledby="verify-title">
          <span className="mark mark-atlas auth-mark">A</span>
          <span className="eyebrow">FFP HOLDINGS</span>
          <h1 id="verify-title">Verify your email</h1>
          <p>
            {user.email} must be verified before Atlas will let it in. Use the
            verification link we email you, then come back and press the check
            button.
          </p>
          {notice && <div className="auth-error" role="status" style={{ borderColor: "#b8d8c8", background: "#edf9f2", color: "#1e6347" }}>{notice}</div>}
          <button className="primary-button auth-button" type="button" disabled={busy} onClick={() => void resendVerification()}>
            Send verification email
          </button>
          <button className="compact-button auth-button" type="button" disabled={busy} onClick={() => void recheckAccess()}>
            I have verified, check again
          </button>
          <button className="text-button auth-switch" type="button" onClick={() => void signOut(auth)}>Sign out</button>
        </section>
      </main>
    );
  }

  if (!authorised) {
    return (
      <main className="auth-page">
        <section className="auth-card" aria-labelledby="denied-title">
          <span className="mark mark-atlas auth-mark">A</span>
          <span className="eyebrow">FFP HOLDINGS</span>
          <h1 id="denied-title">Not authorised</h1>
          <p>
            {user.email ?? "This account"} is verified and signed in but not on
            the Atlas access list. Ask the administrator to add it.
          </p>
          <button className="compact-button auth-button" type="button" disabled={busy} onClick={() => void recheckAccess()}>
            Check again
          </button>
          <button className="text-button auth-switch" type="button" onClick={() => void signOut(auth)}>Sign out</button>
        </section>
      </main>
    );
  }

  return <BoardPortal apiFetch={apiFetch} userEmail={user.email ?? "unknown"} onSignOut={() => signOut(auth)} />;
}
